import { useState, useCallback } from 'react';
import { graphQlClient } from '@config/graphqlClient';
import {
  Organization,
  getOrganizationsForTaskCreationRequest,
} from '@models/organization.model';
import OrganizationsGraphQL from '@graphql/organization.queries';
import { useAppDispatch } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { SnackType } from '@models/common.model';
import { useTranslation } from '@hooks/useTranslation';

interface UseOrganizationsReturn {
  organizations: Organization[];
  loading: boolean;
  error: string | null;
  fetchOrganizations: () => Promise<void>;
}

export const useOrganizations = (): UseOrganizationsReturn => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const localeCommon = useTranslation('common');

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data: getOrganizationsForTaskCreationRequest =
        await graphQlClient.request(
          OrganizationsGraphQL.queries.getOrganizationsForTaskCreation,
          {
            organizations: {
              page: 0,
              limit: 1000,
              search: '',
              filter: 'Active',
              orderBy: 'name',
              orderDirection: 'asc',
            },
          },
        );

      if (data.getOrganizationsForTaskCreation.organizations) {
        setOrganizations(data.getOrganizationsForTaskCreation.organizations);
      }
    } catch (e: any) {
      const errorMessage =
        e?.response?.errors[0]?.message || localeCommon['requestError'];
      setError(errorMessage);
      dispatch(
        appendActionMessage({
          message: errorMessage,
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, localeCommon]);

  return {
    organizations,
    loading,
    error,
    fetchOrganizations,
  };
};
